<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <!-- Header Section with Logo, Dashboard, Profile, and Logout -->
    <Header :isDropdownOpen="isDropdownOpen" @toggleDropdown="toggleDropdown" />

    <!-- Sidebar and Main Content -->
    <main :class="mainContentClass">
      <div class="main-header">
        <div class="header-left">
          <h1 class="main-title">Your Bookings</h1>
          <p class="subtitle">Manage your upcoming flight reservations</p>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="reservations.length === 0" class="empty-state-wrapper">
        <div class="empty-state">
          <vue-feather type="calendar" size="48" class="empty-icon" />
          <h2 class="empty-title">No Bookings Yet</h2>
          <p class="empty-message">
            You don't have any flight reservations. Start exploring available
            flights!
          </p>
          <router-link to="/" class="browse-btn"> Browse Flights </router-link>
        </div>
      </div>

      <!-- Reservations container -->
      <div v-if="reservations.length !== 0" class="reservations-container">
        <div
          v-for="reservation in reservations"
          :key="reservation.id"
          class="reservation-card"
        >
          <div class="card-media">
            <img
              :src="reservation.bannerUrl"
              :alt="reservation.eventName"
              class="card-image"
            />
            <div class="date-badge">
              <span class="date-day">
                {{ new Date(reservation.selectedDate).getDate() }}
              </span>
              <span class="date-month">
                {{
                  new Date(reservation.selectedDate).toLocaleString("en-US", {
                    month: "short",
                  })
                }}
              </span>
            </div>
            <div class="status-badge" :class="getStatusClass(reservation)">
              {{ getStatusText(reservation) }}
            </div>
          </div>

          <div class="card-content">
            <div class="content-header">
              <h3 class="card-title text-truncate">
                {{ reservation.eventName }}
              </h3>
              <p class="tour-type">30 min · Remote Drone Tour</p>
            </div>

            <Divider />

            <div class="details-list">
              <div class="detail-row">
                <div class="detail-col">
                  <h4>Date</h4>
                  <p class="text-truncate">
                    {{
                      reservation.eventSchedule?.user?.date || "Not available"
                    }}
                  </p>
                </div>
                <div class="detail-col">
                  <h4>Event Time</h4>
                  <p class="text-truncate time-display">
                    {{
                      reservation.eventSchedule?.user?.time || "Not available"
                    }}
                    <span
                      class="timezone-indicator"
                      v-if="reservation.eventSchedule?.user?.timezone"
                    >
                      {{
                        formatTimezone(
                          reservation.eventSchedule?.user?.timezone
                        )
                      }}
                    </span>
                  </p>
                </div>
              </div>

              <div class="detail-row">
                <div class="detail-col">
                  <h4>Location</h4>
                  <p class="text-truncate">{{ reservation.location }}</p>
                </div>
                <div class="detail-col">
                  <h4>Tickets</h4>
                  <p class="text-truncate">
                    {{ reservation.guestCount || 1 }}
                    {{
                      (reservation.guestCount || 1) === 1 ? "ticket" : "tickets"
                    }}
                  </p>
                </div>
              </div>
            </div>

            <div class="card-footer">
              <p class="arrival-note">
                <vue-feather type="info" size="14" class="info-icon" />
                Please arrive 15 minutes before departure
              </p>
              <button
                class="primary-btn"
                @click="proceedToGate(reservation.roomCode)"
                :disabled="!reservation.roomCode"
              >
                Proceed to Gate
                <vue-feather type="log-in" size="16" class="btn-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  onSnapshot,
  orderBy,
  getDoc,
  doc,
  query,
} from "firebase/firestore";
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import { auth } from "../plugins/firebase";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import Divider from "@/components/organisms/Divider.vue";

export default {
  data() {
    return {
      reservations: [],
    };
  },
  components: {
    Header,
    Footer,
    Divider,
  },
  methods: {
    fetchReservations() {
      const db = getFirestore();
      const reservationsQuery = query(
        collection(db, "reservations"),
        orderBy("createdAt", "desc")
      );

      this.unsubscribe = onSnapshot(reservationsQuery, async (snapshot) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

        const reservations = snapshot.docs
          .filter((doc) => doc.data().createdBy === auth.currentUser.uid)
          .filter((doc) => doc.data().isConfirmed)
          .filter((doc) => doc.data().isCancelled === false)
          .filter((doc) => {
            const selectedDate = new Date(doc.data().selectedDate);
            selectedDate.setHours(0, 0, 0, 0);
            return selectedDate >= today; // Only show future and today's bookings
          })
          .map((doc) => {
            return {
              id: doc.id,
              ...doc.data(),
            };
          });

        // Get unique listing IDs
        const uniqueListingIds = [
          ...new Set(reservations.map((reservation) => reservation.listingId)),
        ];

        // Fetch listings details
        const listingsPromises = uniqueListingIds.map(async (listingId) => {
          const listingDoc = await getDoc(doc(db, "listings", listingId));
          return { id: listingDoc.id, ...listingDoc.data() };
        });

        const listings = await Promise.all(listingsPromises);

        this.reservations = reservations
          // .filter((reservation) => reservation.roomCode !== null)
          .map((reservation) => {
            const listingDetails =
              listings.find(
                (listing) => listing.id === reservation.listingId
              ) || {};
            return {
              ...reservation,
              eventName: listingDetails.title || "Unnamed Event",
              location: listingDetails.location,
              bannerUrl: listingDetails.bannerUrl,
              eventDate: reservation.createdAt
                .toDate()
                .toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
              // eventTime: reservation.createdAt.toDate().toLocaleTimeString(),
            };
          });
      });
    },
    formatTimezone(timezone) {
      if (!timezone) return "";

      // Handle case where timezone might be abbreviated
      if (timezone.includes("/")) {
        // For full timezone names like "Asia/Calcutta"
        const parts = timezone.split("/");
        if (parts.length > 1) {
          return parts[0] + "/" + parts[1]; // Return the full path
        }
      }

      return timezone; // Return as is if not in expected format
    },
    proceedToGate(roomCode) {
      console.log("Proceed to gate:", roomCode);
      this.$router.push({
        path: `/${roomCode}`,
      });
    },
    getStatusClass(reservation) {
      if (reservation.isCancelled) return "status-cancelled";
      if (!reservation.isConfirmed) return "status-unconfirmed";
      if (reservation.roomCode) return "status-active";
      return "status-pending";
    },
    getStatusText(reservation) {
      if (reservation.isCancelled) return "Cancelled";
      if (!reservation.isConfirmed) return "Unconfirmed";
      if (reservation.roomCode) return "Ready to Board";
      return "Upcoming";
    },
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });

    const listingCoverClass = computed(() => {
      return isMobile() ? "listing-cover-mobile" : "listing-cover-web";
    });

    return {
      mainContentClass,
      listingCoverClass,
    };
  },
  mounted() {
    this.fetchReservations();
  },
  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
};
</script>

<style scoped>
/* Container and layout */
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Main Content */
.mobile-main-content {
  margin-top: 20px;
  padding: 30px;
}

.web-main-content {
  margin-top: 6rem;
  margin-bottom: 20rem;
  padding: 2rem 10rem;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.main-title {
  font-size: 3rem;
  color: #000;
}

.subtitle {
  color: #64748b;
  font-size: 1rem;
}

.main-btn {
  background-color: #000;
  color: white;
  padding: 0.5rem 1rem;
  height: 45px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.main-btn:disabled {
  background-color: #ccc;
}

.main-btn:disabled:hover {
  background-color: #ccc;
}

.main-btn:hover {
  background-color: #679ef6;
}

.logout-button {
  background-color: #e5e7eb !important;
  border: none;
  border-radius: 12px;
  color: white;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.logout-button:hover {
  background-color: rgb(235, 235, 235) !important;
}

.empty-state-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 600px;
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 2rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 400px;
  padding: 2rem;
  border-radius: 16px;
}

.empty-icon {
  color: #94a3b8;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.empty-message {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
}

.browse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background: #000;
  color: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s;
}

.browse-btn:hover {
  background: #1a1a1a;
}

.reservations-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.reservation-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-media {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/9;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.reservation-card:hover .card-image {
  transform: scale(1.05);
}

.date-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  text-align: center;
  backdrop-filter: blur(4px);
}

.date-day {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: #0f172a;
}

.date-month {
  display: block;
  font-size: 0.875rem;
  color: #64748b;
  text-transform: uppercase;
}

.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.status-cancelled {
  background: rgba(239, 68, 68, 0.9);
  color: white;
}

.status-unconfirmed {
  background: rgba(245, 158, 11, 0.9);
  color: white;
}

.status-active {
  background: rgba(220, 252, 231, 0.9);
  color: #15803d;
}

.status-pending {
  background: rgba(254, 249, 195, 0.9);
  color: #92400e;
}

.card-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
}

.content-header {
  min-height: 3.5rem;
}

.card-title {
  font-size: 1.5rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.tour-type {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.detail-row {
  display: flex;
  gap: 2rem;
}

.detail-col {
  flex: 1;
  min-width: 0;
}

.detail-col h4 {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
  font-weight: 500;
}

.detail-col p {
  font-size: 1rem;
  color: #111827;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}

.card-footer {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.arrival-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 0.75rem 0;
  min-height: auto;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.primary-btn {
  width: 100%;
  padding: 0.75rem;
  height: 2.75rem;
  background: #000;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.primary-btn:hover {
  background: #1a1a1a;
}

.primary-btn:disabled {
  background: #e5e7eb;
  cursor: not-allowed;
}

.timezone-indicator {
  font-size: 0.75rem;
  color: #9ca3af;
  display: block;
  font-weight: normal;
  margin-top: 1px;
}

.time-display {
  line-height: 1.3;
}

/* This ensures the time and timezone are properly aligned */
.detail-col p.time-display {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .web-main-content {
    padding: 1.5rem;
    margin-top: 4rem;
    margin-bottom: 4rem;
  }
}

@media (max-width: 640px) {
  .card-content {
    padding: 1rem;
    gap: 0.75rem;
  }

  .detail-row {
    flex-direction: column;
    gap: 0.75rem;
  }

  .card-footer {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }

  .empty-state-wrapper {
    padding: 1rem;
    min-height: 400px;
  }

  .empty-state {
    padding: 1.5rem;
  }
}
</style>
